import { Redirect } from 'expo-router';
import { Megaphone, Plus } from 'lucide-react-native';
import { useCallback, useMemo, useState } from 'react';
import { ActivityIndicator, FlatList, StyleSheet, View } from 'react-native';
import { FadeIn, sectionEntrance } from '../../src/components/animations';
import { Box, HStack, Text, VStack } from '../../src/components/primitives';
import { Button } from '../../src/components/primitives/Button';
import { EmptyState } from '../../src/components/primitives/EmptyState';
import { SearchInput } from '../../src/components/primitives/SearchInput';
import { useAuth } from '../../src/contexts/AuthContext';
import { useTranslation } from '../../src/contexts/TranslationContext';
import { CampaignListItem, CreateCampaignSheet } from '../../src/features/campaigns/components';
import { useCampaigns, useCreateCampaign } from '../../src/features/campaigns/hooks/useCampaigns';
import {
  type Campaign,
  CampaignStatus,
  type CreateCampaignInput,
} from '../../src/features/campaigns/types';
import { isCampaignsEnabled } from '../../src/lib/featureFlags';
import { colors } from '../../src/theme/colors';

function CampaignSeparator() {
  return <Box style={separatorStyle} />;
}

const separatorStyle = { height: 8 };

const STATUS_FILTERS = [
  { value: '', labelKey: 'common.sources.allSources' },
  { value: CampaignStatus.DRAFT, labelKey: 'campaigns.status.draft' },
  { value: CampaignStatus.ACTIVE, labelKey: 'campaigns.status.active' },
  { value: CampaignStatus.COMPLETED, labelKey: 'campaigns.status.completed' },
] as const;

function CampaignsScreen() {
  const { currentOrganization } = useAuth();
  const { t } = useTranslation();

  const orgId = currentOrganization?.id;

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [showCreateSheet, setShowCreateSheet] = useState(false);

  const queryParams = useMemo(
    () => ({
      organizationId: orgId ?? '',
      search: search || undefined,
      status: (statusFilter as CampaignStatus) || undefined,
      limit: 100,
    }),
    [orgId, search, statusFilter],
  );

  const { data, isLoading } = useCampaigns(queryParams);
  const createCampaign = useCreateCampaign();

  const campaigns = data?.campaigns ?? [];

  const handleCreateCampaign = useCallback(
    (input: CreateCampaignInput) => {
      createCampaign.mutate(input, {
        onSuccess: () => setShowCreateSheet(false),
      });
    },
    [createCampaign],
  );

  const handleCampaignPress = useCallback((_campaign: Campaign) => {
    // Detail view will be added in a future phase
  }, []);

  return (
    <View style={{ flex: 1 }}>
      <Box flex={1} backgroundColor={colors.background.primary} padding="lg">
        <VStack spacing="lg" style={styles.container}>
          <FadeIn {...sectionEntrance(0)}>
            <HStack spacing="md" align="center" justify="between">
              <VStack spacing="xxs">
                <Text variant="h2" color={colors.text.primary}>
                  {t('campaigns.title')}
                </Text>
              </VStack>
              <Button variant="primary" size="sm" onPress={() => setShowCreateSheet(true)}>
                <HStack spacing="xs" align="center">
                  <Plus size={14} color={colors.background.primary} strokeWidth={2.5} />
                  <Text
                    variant="caption"
                    color={colors.background.primary}
                    style={styles.buttonText}
                  >
                    {t('campaigns.createNew')}
                  </Text>
                </HStack>
              </Button>
            </HStack>
          </FadeIn>

          <FadeIn {...sectionEntrance(1)}>
            <HStack spacing="sm" align="center" style={styles.filtersRow}>
              <Box style={styles.searchBox}>
                <SearchInput
                  value={search}
                  onChangeText={setSearch}
                  placeholder={`${t('campaigns.title')}...`}
                />
              </Box>
              <HStack spacing="xs">
                {STATUS_FILTERS.map((filter) => {
                  const isActive =
                    filter.value === statusFilter || (filter.value === '' && statusFilter === '');
                  return (
                    <Box
                      key={filter.value || 'all'}
                      pressable
                      onPress={() => setStatusFilter(filter.value)}
                      borderRadius="full"
                      style={[styles.filterChip, isActive && styles.filterChipActive]}
                    >
                      <Text
                        variant="caption"
                        color={isActive ? colors.primary[400] : colors.text.tertiary}
                        style={styles.filterText}
                      >
                        {filter.value === '' ? t('common.sources.allSources') : t(filter.labelKey)}
                      </Text>
                    </Box>
                  );
                })}
              </HStack>
            </HStack>
          </FadeIn>

          {isLoading && (
            <Box flex={1} alignItems="center" justifyContent="center" style={styles.loadingBox}>
              <ActivityIndicator size="large" color={colors.primary[500]} />
            </Box>
          )}
          {!isLoading && campaigns.length === 0 && (
            <EmptyState
              icon={<Megaphone size={32} color={colors.text.tertiary} strokeWidth={1.5} />}
              title={t('campaigns.empty.title')}
              description={t('campaigns.empty.description')}
            />
          )}
          {!isLoading && campaigns.length > 0 && (
            <FlatList
              data={campaigns}
              keyExtractor={(item) => item.id}
              renderItem={({ item }) => (
                <CampaignListItem campaign={item} onPress={handleCampaignPress} />
              )}
              ItemSeparatorComponent={CampaignSeparator}
              contentContainerStyle={styles.listContent}
              showsVerticalScrollIndicator={false}
              style={styles.list}
            />
          )}
        </VStack>
      </Box>

      {orgId && (
        <CreateCampaignSheet
          visible={showCreateSheet}
          onClose={() => setShowCreateSheet(false)}
          onSubmit={handleCreateCampaign}
          organizationId={orgId}
          isLoading={createCampaign.isPending}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  buttonText: {
    fontWeight: '700',
  },
  filtersRow: {
    flexWrap: 'wrap',
  },
  searchBox: {
    flex: 1,
    minWidth: 200,
    maxWidth: 320,
  },
  filterChip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: colors.background.secondary,
    borderWidth: 1,
    borderColor: colors.border.default,
  },
  filterChipActive: {
    backgroundColor: colors.primary.muted,
    borderColor: colors.primary.subtle,
  },
  filterText: {
    fontWeight: '600',
    fontSize: 12,
  },
  loadingBox: {
    minHeight: 200,
  },
  list: {
    flex: 1,
  },
  listContent: {
    paddingBottom: 24,
  },
});

export default function CampaignsRoute() {
  if (!isCampaignsEnabled()) {
    return <Redirect href="/dashboard" />;
  }

  return <CampaignsScreen />;
}
